/**
 * P_WEEKLY_REPORT_CARD_3.0 — Recognition tab.
 *
 * Admin curation surface for the immutable recognition layer:
 *   1st Star Of The Week — auto-suggested from positive comments
 *     + guest mentions; admin Accepts / Edits / Replaces / Skips.
 *   Customer Comment Of The Week — auto-suggested from rating ≥ 4
 *     comments preferring substance; admin Accepts / Replaces / Skips.
 *
 * Once status=accepted, generate-now bakes the recognition into the
 * immutable snapshot. Re-send never recomputes.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { weeklyRecognitionAPI, weeklyReportAPI } from '../../lib/api';

// Default = last completed Sunday in UTC.
const lastCompletedSunday = () => {
    const today = new Date();
    const day = today.getUTCDay(); // 0 = Sunday
    const daysBack = day === 0 ? 7 : day;
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - daysBack));
    return d.toISOString().slice(0, 10);
};

export default function WeeklyReportRecognition({ organizationId, t }) {
    const [weekEnd, setWeekEnd] = useState(lastCompletedSunday());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    // Local editable state
    const [star, setStar] = useState(null);
    const [comment, setComment] = useState(null);
    const [accolades, setAccolades] = useState(null);

    const load = async () => {
        if (!organizationId) return;
        setLoading(true);
        try {
            const res = await weeklyRecognitionAPI.get(organizationId, weekEnd);
            setData(res.data);
            const sStored = res.data?.stored?.star;
            const cStored = res.data?.stored?.comment;
            const aStored = res.data?.stored?.accolades;
            setStar(sStored || res.data?.suggestions?.star || null);
            setComment(cStored || res.data?.suggestions?.comment || null);
            setAccolades(aStored || res.data?.suggestions?.accolades || null);
        } catch {
            toast.error(t('weeklyReport.states.error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [organizationId, weekEnd]);

    const persist = async (nextStar, nextComment, nextAccolades) => {
        setBusy(true);
        try {
            await weeklyRecognitionAPI.upsert({
                organization_id: organizationId,
                week_end: weekEnd,
                star: nextStar,
                comment: nextComment,
                accolades: nextAccolades,
            });
            toast.success(t('weeklyReport.recognition.saved'));
            // Re-bake the snapshot so the change reaches the portal immediately.
            try { await weeklyReportAPI.generateNow(organizationId, weekEnd); } catch { /* non-fatal */ }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('weeklyReport.states.error'));
        } finally {
            setBusy(false);
        }
    };

    const acceptStar = (s) => persist({ ...s, status: 'accepted' }, comment, accolades);
    const skipStar = () => persist({ status: 'skipped' }, comment, accolades);
    const acceptComment = (c) => persist(star, { ...c, status: 'accepted' }, accolades);
    const skipComment = () => persist(star, { status: 'skipped' }, accolades);
    const acceptAccolades = (a) => persist(star, comment, { ...a, status: 'accepted' });
    const skipAccolades = () => persist(star, comment, { status: 'skipped', items: [] });

    if (loading || !data) {
        return (
            <div className="py-32 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin opacity-50" />
            </div>
        );
    }

    const sugg = data.suggestions || {};
    // v3.3 — manager-stored override > suggestion cascade.
    const logoUrl = data?.stored?.logo_url || sugg.logo_url || null;

    return (
        <section data-testid="weekly-report-recognition">
            <h2 className="text-xs sm:text-sm uppercase tracking-[0.32em] opacity-60 mb-12">
                {t('weeklyReport.recognition.title')}
            </h2>

            <label className="inline-flex items-center gap-3 mb-16 text-sm opacity-70">
                <span className="text-xs uppercase tracking-[0.24em] opacity-60">{t('weeklyReport.recognition.weekEnd')}</span>
                <input
                    type="date"
                    value={weekEnd}
                    onChange={(e) => setWeekEnd(e.target.value)}
                    data-testid="weekly-report-recognition-week-input"
                    className="bg-transparent border-b border-white/20 px-1 py-1 text-sm focus:outline-none focus:border-white/60"
                />
            </label>

            {/* Recognition Logo (location → org cascade) */}
            {logoUrl && (
                <div className="mb-16 flex justify-center" data-testid="weekly-report-recognition-logo">
                    <img
                        src={logoUrl}
                        alt="Organization Logo"
                        className="max-h-20 w-auto opacity-90"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                </div>
            )}

            {/* 1st Star block */}
            <div className="mb-24 pb-16 border-b border-white/10" data-testid="weekly-report-recognition-star-block">
                <h3 className="text-3xl sm:text-4xl font-light tracking-tight mb-6">
                    {t('weeklyReport.recognition.starTitle')}
                </h3>

                {star?.status === 'accepted' ? (
                    <AcceptedBanner
                        label={t('weeklyReport.recognition.starAccepted', { name: star.name })}
                        onChange={() => setStar({ ...star, status: 'suggested' })}
                        onSkip={skipStar}
                        t={t}
                    />
                ) : star?.status === 'skipped' ? (
                    <SkippedBanner
                        label={t('weeklyReport.recognition.starSkipped')}
                        onUnskip={() => setStar(sugg.star || { status: 'suggested' })}
                        t={t}
                    />
                ) : (
                    <StarEditor
                        value={star}
                        suggestion={sugg.star}
                        onChange={setStar}
                        onAccept={() => acceptStar(star)}
                        onSkip={skipStar}
                        busy={busy}
                        t={t}
                    />
                )}
            </div>

            {/* Positive Accolades block (v3.3) */}
            <div className="mb-24 pb-16 border-b border-white/10" data-testid="weekly-report-recognition-accolades-block">
                <h3 className="text-3xl sm:text-4xl font-light tracking-tight mb-6">
                    {t('weeklyReport.recognition.accoladesTitle', 'Positive Accolades')}
                </h3>

                {accolades?.status === 'accepted' ? (
                    <AcceptedBanner
                        label={t('weeklyReport.recognition.accoladesAccepted', 'Accolades accepted')}
                        onChange={() => setAccolades({ ...accolades, status: 'suggested' })}
                        onSkip={skipAccolades}
                        t={t}
                    />
                ) : accolades?.status === 'skipped' ? (
                    <SkippedBanner
                        label={t('weeklyReport.recognition.accoladesSkipped', 'Accolades skipped this week')}
                        onUnskip={() => setAccolades(sugg.accolades || { status: 'suggested', items: [] })}
                        t={t}
                    />
                ) : (
                    <AccoladesEditor
                        value={accolades}
                        suggestion={sugg.accolades}
                        onChange={setAccolades}
                        onAccept={() => acceptAccolades(accolades)}
                        onSkip={skipAccolades}
                        busy={busy}
                        t={t}
                    />
                )}
            </div>

            {/* Customer Comment block */}
            <div data-testid="weekly-report-recognition-comment-block">
                <h3 className="text-3xl sm:text-4xl font-light tracking-tight mb-6">
                    {t('weeklyReport.recognition.commentTitle')}
                </h3>

                {comment?.status === 'accepted' ? (
                    <AcceptedBanner
                        label={t('weeklyReport.recognition.commentAccepted')}
                        onChange={() => setComment({ ...comment, status: 'suggested' })}
                        onSkip={skipComment}
                        t={t}
                    />
                ) : comment?.status === 'skipped' ? (
                    <SkippedBanner
                        label={t('weeklyReport.recognition.commentSkipped')}
                        onUnskip={() => setComment(sugg.comment || { status: 'suggested', text: '' })}
                        t={t}
                    />
                ) : (
                    <CommentEditor
                        value={comment}
                        suggestion={sugg.comment}
                        onChange={setComment}
                        onAccept={() => acceptComment(comment)}
                        onSkip={skipComment}
                        busy={busy}
                        t={t}
                    />
                )}
            </div>
        </section>
    );
}

const StarEditor = ({ value, suggestion, onChange, onAccept, onSkip, busy, t }) => {
    const v = value || {};
    const set = (k, val) => onChange({ ...v, [k]: val });
    return (
        <div className="space-y-8">
            {suggestion && (
                <p className="text-sm opacity-60 leading-relaxed max-w-xl">
                    {t('weeklyReport.recognition.starSuggestionHint', {
                        name: suggestion.name,
                        mentions: suggestion.mentions,
                    })}
                </p>
            )}
            <Field label={t('weeklyReport.recognition.fieldName')} value={v.name || ''} onChange={(x) => set('name', x)} testId="weekly-report-recognition-star-name" />
            <Field label={t('weeklyReport.recognition.fieldTitle')} value={v.title || ''} onChange={(x) => set('title', x)} testId="weekly-report-recognition-star-title" />
            <Field label={t('weeklyReport.recognition.fieldStory')} value={v.story || ''} onChange={(x) => set('story', x)} testId="weekly-report-recognition-star-story" textarea />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <Field label={t('weeklyReport.recognition.fieldRecoveries')} type="number" value={v.recoveries ?? ''} onChange={(x) => set('recoveries', x === '' ? null : Number(x))} testId="weekly-report-recognition-star-recoveries" />
                <Field label={t('weeklyReport.recognition.fieldMentions')} type="number" value={v.mentions ?? ''} onChange={(x) => set('mentions', x === '' ? null : Number(x))} testId="weekly-report-recognition-star-mentions" />
                <Field label={t('weeklyReport.recognition.fieldSatisfaction')} type="number" value={v.satisfaction ?? ''} onChange={(x) => set('satisfaction', x === '' ? null : Number(x))} testId="weekly-report-recognition-star-satisfaction" />
            </div>
            <ActionRow
                onAccept={onAccept}
                onSkip={onSkip}
                acceptLabel={t('weeklyReport.recognition.accept')}
                skipLabel={t('weeklyReport.recognition.skip')}
                busy={busy}
                disabled={!(v.name || '').trim()}
                testIdPrefix="weekly-report-recognition-star"
            />
        </div>
    );
};

const CommentEditor = ({ value, suggestion, onChange, onAccept, onSkip, busy, t }) => {
    const v = value || {};
    return (
        <div className="space-y-8">
            {suggestion && (
                <button
                    type="button"
                    onClick={() => onChange({ ...suggestion, status: 'suggested' })}
                    data-testid="weekly-report-recognition-comment-use-suggestion"
                    className="text-left p-5 rounded-xl border border-white/10 hover:border-white/30 transition-colors w-full"
                >
                    <p className="text-[11px] uppercase tracking-[0.24em] opacity-50 mb-2">
                        {t('weeklyReport.recognition.suggestion')}
                    </p>
                    <p className="text-base font-light italic">&ldquo;{suggestion.text}&rdquo;</p>
                </button>
            )}
            <Field
                label={t('weeklyReport.recognition.fieldComment')}
                value={v.text || ''}
                onChange={(x) => onChange({ ...v, text: x, status: 'suggested' })}
                testId="weekly-report-recognition-comment-text"
                textarea
            />
            <ActionRow
                onAccept={onAccept}
                onSkip={onSkip}
                acceptLabel={t('weeklyReport.recognition.accept')}
                skipLabel={t('weeklyReport.recognition.skip')}
                busy={busy}
                disabled={!(v.text || '').trim()}
                testIdPrefix="weekly-report-recognition-comment"
            />
        </div>
    );
};

const Field = ({ label, value, onChange, type = 'text', testId, textarea }) => (
    <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.24em] opacity-50 mb-2">{label}</span>
        {textarea ? (
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                rows={3}
                className="w-full bg-transparent border-b border-white/20 px-1 py-2 text-base focus:outline-none focus:border-white/60 resize-none"
            />
        ) : (
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="w-full bg-transparent border-b border-white/20 px-1 py-2 text-base focus:outline-none focus:border-white/60"
            />
        )}
    </label>
);

const ActionRow = ({ onAccept, onSkip, acceptLabel, skipLabel, busy, disabled, testIdPrefix }) => (
    <div className="flex items-center gap-3 pt-4">
        <button
            type="button"
            onClick={onAccept}
            disabled={busy || disabled}
            data-testid={`${testIdPrefix}-accept-btn`}
            className="px-4 py-2 text-xs uppercase tracking-[0.2em] border border-emerald-300/60 text-emerald-300/90 rounded-full hover:bg-emerald-300/10 transition-colors disabled:opacity-40"
        >
            {acceptLabel}
        </button>
        <button
            type="button"
            onClick={onSkip}
            disabled={busy}
            data-testid={`${testIdPrefix}-skip-btn`}
            className="px-4 py-2 text-xs uppercase tracking-[0.2em] opacity-60 hover:opacity-100 transition-opacity"
        >
            {skipLabel}
        </button>
    </div>
);

const AcceptedBanner = ({ label, onChange, onSkip, t }) => (
    <div className="space-y-4">
        <p className="text-base opacity-80">✓ {label}</p>
        <div className="flex gap-3">
            <button
                type="button"
                onClick={onChange}
                className="px-3 py-1.5 text-xs uppercase tracking-[0.2em] border border-white/30 rounded-full hover:border-white/80 transition-colors"
            >
                {t('weeklyReport.recognition.edit')}
            </button>
            <button
                type="button"
                onClick={onSkip}
                className="px-3 py-1.5 text-xs uppercase tracking-[0.2em] opacity-60 hover:opacity-100"
            >
                {t('weeklyReport.recognition.skip')}
            </button>
        </div>
    </div>
);

const SkippedBanner = ({ label, onUnskip, t }) => (
    <div className="space-y-4">
        <p className="text-base opacity-50">{label}</p>
        <button
            type="button"
            onClick={onUnskip}
            className="px-3 py-1.5 text-xs uppercase tracking-[0.2em] border border-white/30 rounded-full hover:border-white/80 transition-colors"
        >
            {t('weeklyReport.recognition.restart')}
        </button>
    </div>
);


// P_WEEKLY_REPORT_CARD_3.3 — Positive Accolades editor.
// Pre-populated from the suggestion engine; manager accepts or
// edits the bulleted list with one click. No blank-form authoring.
const AccoladesEditor = ({ value, suggestion, onChange, onAccept, onSkip, busy, t }) => {
    const items = (value?.items?.length ? value.items : (suggestion?.items || []));
    const hasItems = items.length > 0;

    const removeAt = (idx) => {
        const next = items.filter((_, i) => i !== idx);
        onChange({ ...(value || {}), items: next, status: 'suggested' });
    };

    if (!hasItems) {
        return (
            <div className="space-y-4">
                <p className="text-base opacity-60">
                    {t('weeklyReport.recognition.accoladesEmpty',
                       "No positive themes surfaced this week. Skip or revisit next week.")}
                </p>
                <button
                    type="button"
                    onClick={onSkip}
                    disabled={busy}
                    data-testid="weekly-report-recognition-accolades-skip"
                    className="px-3 py-1.5 text-xs uppercase tracking-[0.2em] opacity-60 hover:opacity-100"
                >
                    {t('weeklyReport.recognition.skip')}
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="weekly-report-recognition-accolades-editor">
            <p className="text-sm opacity-70">{t('weeklyReport.recognition.accoladesIntro', 'Guests praised:')}</p>
            <ul className="space-y-3">
                {items.map((it, idx) => (
                    <li key={`${it.label}-${idx}`}
                        className="flex items-center justify-between text-2xl sm:text-3xl font-light tracking-tight">
                        <span>
                            <span className="opacity-30 pr-4">•</span>
                            {it.label}
                            {typeof it.mentions === 'number' && it.mentions > 0 && (
                                <span className="ml-3 text-xs opacity-40 tracking-[0.2em] uppercase">
                                    {it.mentions} {t('weeklyReport.recognition.mentions', 'mentions')}
                                </span>
                            )}
                        </span>
                        <button
                            type="button"
                            onClick={() => removeAt(idx)}
                            className="text-xs uppercase tracking-[0.2em] opacity-40 hover:opacity-100"
                            data-testid={`weekly-report-recognition-accolade-remove-${idx}`}
                            title={t('weeklyReport.recognition.remove', 'Remove')}
                        >
                            ✕
                        </button>
                    </li>
                ))}
            </ul>
            <div className="flex items-center gap-3 pt-2">
                <button
                    type="button"
                    onClick={onAccept}
                    disabled={busy}
                    data-testid="weekly-report-recognition-accolades-accept"
                    className="px-4 py-2 text-xs uppercase tracking-[0.2em] border border-white/40 rounded-full hover:border-white/80 transition-colors"
                >
                    ✓ {t('weeklyReport.recognition.accept', 'Accept')}
                </button>
                <button
                    type="button"
                    onClick={onSkip}
                    disabled={busy}
                    data-testid="weekly-report-recognition-accolades-skip"
                    className="px-3 py-1.5 text-xs uppercase tracking-[0.2em] opacity-60 hover:opacity-100"
                >
                    {t('weeklyReport.recognition.skip', 'Skip This Week')}
                </button>
            </div>
        </div>
    );
};

